import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/auth/AuthPages.css';

/**
 * AuthCallbackPage - Handle OAuth callback with token
 *
 * Backend redirects to: /auth/callback?token={jwt}
 * This page stores the token and redirects to the app.
 */
export const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const errorParam = searchParams.get('error');

  const { handleOAuthCallback } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const processingRef = useRef(false);

  useEffect(() => {
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      return;
    }

    if (!token) {
      setError('Aucun token reçu. La connexion a échoué.');
      return;
    }

    // Prevent double processing in StrictMode
    if (processingRef.current) return;
    processingRef.current = true;

    // Validate redirect path is safe
    const isValidRedirectPath = (path: string): boolean => {
      if (!path || !path.startsWith('/') || path.startsWith('//')) return false;
      if (path.includes(':')) return false;
      return true;
    };

    // Process the OAuth callback
    handleOAuthCallback(token)
      .then(result => {
        if (result.success) {
          let redirectPath = sessionStorage.getItem('auth_redirect') || '/';
          // Safety net: validate path and don't redirect back to auth pages
          if (!isValidRedirectPath(redirectPath) ||
              redirectPath.startsWith('/login') ||
              redirectPath.startsWith('/auth') ||
              redirectPath.startsWith('/register')) {
            redirectPath = '/';
          }
          sessionStorage.removeItem('auth_redirect');
          navigate(redirectPath, { replace: true });
        } else {
          setError(result.error || 'La connexion a échoué.');
        }
      })
      .catch(err => {
        sessionStorage.removeItem('auth_redirect');
        setError(err?.message || 'La connexion a échoué.');
      });
  }, [token, errorParam, handleOAuthCallback, navigate]);

  if (error) {
    return (
      <div className="auth-page">
        <div className="auth-box">
          <header className="auth-header">
            <h1 className="auth-logo">Corpus</h1>
            <h2 className="auth-title">Erreur de connexion</h2>
          </header>

          <div className="auth-alert error" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Connexion échouée</p>
              <p className="auth-alert-message">{error}</p>
            </div>
          </div>

          <div className="auth-links">
            <a href="/login" className="auth-link">
              Retour à la connexion
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Loading state while processing callback
  return (
    <div className="auth-page">
      <div className="auth-box">
        <header className="auth-header">
          <h1 className="auth-logo">Corpus</h1>
          <h2 className="auth-title">Connexion en cours...</h2>
        </header>

        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <span className="auth-spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
        </div>

        <p className="auth-subtitle" style={{ textAlign: 'center' }}>
          Veuillez patienter pendant que nous finalisons votre connexion.
        </p>
      </div>
    </div>
  );
};
